import { type TlbSignedUserState } from '../../reducers/tlbUserReducer';
import { getDbSyncer } from '../LocalDB/DbSyncer';
import { type LocalDB } from '../LocalDB/LocalDB';
import { userIsCrew } from '../Users/User';
import { whenOnline } from '../util/whenOnline';
import { loadAirports } from './Datasources/Airports';
import { cleanupSupplementalData } from './Datasources/Cleanup';
import { loadExternalDocuments } from './Datasources/ExternalDocuments';
import { loadFlightOrders } from './Datasources/FlightOrders';
import { loadFuelPlans } from './Datasources/FuelPlans';
import { type ReservationsSyncOptions } from './Datasources/Reservations';
import { loadReservations } from './Datasources/Reservations';
import { getEventsWithinTafLifetime, loadMetars, loadTafs } from './Datasources/Taf';
import { type UpcomingReservationEvent } from './Datasources/useUpcomingReservations';
import { getUpcomingReservationEvents } from './Datasources/useUpcomingReservations';

export async function updateSupplementalData(
    db: LocalDB,
    user: TlbSignedUserState,
    reservationsSyncOptions: ReservationsSyncOptions
): Promise<boolean> {
    try {
        const dbSyncer = getDbSyncer();
        if (dbSyncer.currentSync?.promise) {
            await dbSyncer.currentSync.promise;
        }
        await whenOnline();
        const userData = user.data;
        if (!userIsCrew(userData)) {
            return false;
        }
        const primaryPromises: Array<Promise<any>> = [
            loadReservations(userData.contactId, db, reservationsSyncOptions),
            loadFlightOrders(userData.contactId, db),
            loadExternalDocuments(db, userData.userId),
        ];
        await Promise.all(primaryPromises);
        await cleanupSupplementalData(db);
        const upcomingEvents = await getUpcomingReservationEvents(userData.contactId, 14 * 24);
        const icaos = getIcaosOfUpcomingEvents(upcomingEvents);
        const weatherIcaos = getIcaosOfUpcomingEvents(getEventsWithinTafLifetime(upcomingEvents));
        const promisesRequiringUpcomingEvents: Array<Promise<any>> = [
            loadAirports(db, icaos),
            loadFuelPlans(db, upcomingEvents),
            loadTafs(db, weatherIcaos),
            loadMetars(db, weatherIcaos),
        ];

        await Promise.all(promisesRequiringUpcomingEvents);

        return true;
    } catch (error) {
        console.error('Error fetching supplemental data');
        console.error(error);

        return false;
    }
}

function getIcaosOfUpcomingEvents(upcomingEvents: UpcomingReservationEvent[]): string[] {
    const icaoArray = upcomingEvents
        .flatMap((e) => [e.event.location, e.event.locationEnd, ...(e.event.alternateAirports || [])])
        .filter((i) => i && i.length >= 4 && i.length <= 6) as string[];

    return [...new Set(icaoArray)];
}
